/**
 * TrackRecordPanel — Sprint 20 reusable P13 component.
 *
 * Standardized "track record" block shown at the bottom of every P13-retrofitted
 * page. Shows hit rate over 30d and 90d, sample size, trajectory sparkline,
 * and recent calls with outcomes.
 *
 * Props:
 *   metric_name (required) — e.g. "Regime Classifier", "Source Credibility"
 *   hit_rate_30d — 0-100 number or null
 *   hit_rate_90d — 0-100 number or null
 *   sample_size — total resolved calls
 *   trajectory — array of { date, value }
 *   recent_calls — array of { call, outcome, date, asset? }
 *   loading — optional boolean
 *
 * Always renders (never null). Empty state shows "Collecting data — X samples
 * so far" rather than hiding.
 */

import { Activity } from 'lucide-react';
import ConvictionSparkline from './ConvictionSparkline.jsx';

const OUTCOME_COLORS = {
  correct:   '#10b981',
  wrong:     '#ef4444',
  partial:   '#f59e0b',
  breakeven: '#6b7280',
  pending:   '#6b7280',
  expired:   '#6b7280',
};

function fmtHit(x) {
  if (x == null) return '—';
  return `${Math.round(x)}%`;
}

function hitColor(x) {
  if (x == null) return '#6b7280';
  if (x >= 60) return '#10b981';
  if (x >= 50) return '#f59e0b';
  return '#ef4444';
}

export default function TrackRecordPanel({
  metric_name,
  hit_rate_30d = null,
  hit_rate_90d = null,
  sample_size = 0,
  trajectory = [],
  recent_calls = [],
  loading = false,
}) {
  return (
    <div className="bg-korinth-surface border-l-4 border-l-purple-400 border border-korinth-border rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-purple-400" />
        <span className="text-xs font-mono font-bold uppercase tracking-wider text-purple-400">
          Track Record · {metric_name}
        </span>
        {sample_size > 0 && (
          <span className="text-[10px] font-mono text-korinth-muted ml-auto">
            {sample_size} resolved call{sample_size === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-[11px] font-mono text-korinth-muted py-1">
          <span className="animate-pulse">● </span>Loading track record...
        </div>
      ) : sample_size === 0 ? (
        <div className="text-[11px] font-mono text-korinth-muted leading-relaxed">
          Collecting data — 0 resolved calls so far.
          <div className="text-[10px] text-korinth-muted/70 mt-1">
            Hit rate will populate as KAIROS predictions resolve. Check back after a few days of live signals.
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <div>
              <div className="text-[9px] font-mono text-korinth-muted">30-day hit</div>
              <div className="text-xl font-mono font-bold" style={{ color: hitColor(hit_rate_30d) }}>
                {fmtHit(hit_rate_30d)}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-mono text-korinth-muted">90-day hit</div>
              <div className="text-xl font-mono font-bold" style={{ color: hitColor(hit_rate_90d) }}>
                {fmtHit(hit_rate_90d)}
              </div>
            </div>
            {trajectory.length > 1 && (
              <div className="ml-auto">
                <div className="text-[9px] font-mono text-korinth-muted text-right">trajectory</div>
                <ConvictionSparkline data={trajectory} stance="neutral" width={140} height={36} />
              </div>
            )}
          </div>

          {recent_calls.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Recent calls</div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {recent_calls.slice(0, 8).map((c, i) => {
                  const color = OUTCOME_COLORS[c.outcome] || '#6b7280';
                  return (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
                      {c.asset && <span className="text-white font-bold w-14 flex-shrink-0">{c.asset}</span>}
                      <span className="text-korinth-text flex-1 truncate">{c.call}</span>
                      <span className="text-[8px] font-bold uppercase px-1 py-0.5 rounded flex-shrink-0" style={{ color, backgroundColor: color + '15' }}>
                        {c.outcome}
                      </span>
                      {c.date && <span className="text-[8px] text-korinth-muted/60 flex-shrink-0">{c.date.slice(5, 16)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
